import { Component, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';
import { StripeErrors } from '@config/errors.config';
import { PaymentService } from '@core/services/payment/payment.service';
import { StripeLoaderService } from '@core/services/stripe-loader/stripe-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { StripeError } from '@stripe/stripe-js';
import { catchError, combineLatest, EMPTY, from, of, Subject, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-pat-payment-panel-stripe-twint',
  template: ``,
  standalone: false,
})
export class PatPaymentPanelStripeTwintComponent implements OnDestroy {
  private readonly paymentService = inject(PaymentService);
  private readonly stripeLoader = inject(StripeLoaderService);
  private readonly paymentStoreDispatcher = inject(PaymentStoreDispatcher);
  private readonly translateService = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  private readonly destroy$ = new Subject<void>();

  private readonly payWithTwint$ = this.paymentService.pay$.pipe(
    switchMap(() =>
      combineLatest({
        paymentIntent: this.paymentService.paymentIntent$,
        stripe: this.stripeLoader.ensureStripeReady$(),
        redirectUrl: this.paymentService.redirectUrl$,
      }),
    ),
    switchMap(({ paymentIntent, stripe, redirectUrl }) => {
      const payment_intent_client_secret = paymentIntent?.payment_intent_client_secret;
      if (!payment_intent_client_secret) {
        return EMPTY;
      }
      const return_url = `${this.document.baseURI}${redirectUrl}`;
      return from(
        stripe.confirmTwintPayment(payment_intent_client_secret, {
          return_url: return_url ?? window.location.href,
          payment_method: {}, // ! this field is mandatory, even if it is empty
        }),
      );
    }),
    tap({
      next: result => {
        if (result.error || !result.paymentIntent) {
          this.handlePaymentError(result.error);
          return;
        }
      },
      error: error => {
        this.handlePaymentError(error);
      },
    }),
    catchError(() => {
      // avoid to break the stream
      return of(null);
    }),
  );

  private readonly enableToPayWithTwint$ = combineLatest({
    paymentIntent: this.paymentService.paymentIntent$,
    stripe: this.stripeLoader.ensureStripeReady$(),
    redirectUrl: this.paymentService.redirectUrl$,
  }).pipe(
    tap(({ paymentIntent, stripe, redirectUrl }) => {
      if (!paymentIntent || !stripe || !redirectUrl) {
        return;
      }
      this.paymentStoreDispatcher.updateUI({
        button: {
          isEnabled: true,
        },
      });
    }),
  );

  private handlePaymentError(error?: StripeError): void {
    const isConnectionError = error?.type === StripeErrors.ApiConnectionError;
    const errorMessage = error?.message ?? this.translateService.instant('payment.unknown-error');

    this.paymentStoreDispatcher.updateUI({
      error: errorMessage,
      button: {
        isEnabled: isConnectionError,
      },
      status: {
        isLoading: false,
        isInProgress: false,
        isRetryInProgress: isConnectionError,
      },
    });
  }

  constructor() {
    this.enableToPayWithTwint$.pipe(takeUntilDestroyed()).subscribe();
    this.payWithTwint$.pipe(takeUntilDestroyed()).subscribe();
    this.paymentStoreDispatcher.updateUI({
      status: {
        isLoading: true,
      },
    });
  }

  ngOnDestroy(): void {
    this.paymentStoreDispatcher.resetUI();
    this.paymentService.paymentIntent$.next(null);

    this.destroy$.next();
    this.destroy$.complete();
  }
}
